/**
 * Rendering provenance.
 *
 * Every fact the system displays and every rule it applies carries its source,
 * and the source renders on screen. There are five values and they are visually
 * distinct. SPEC §3.2.
 *
 * The two that matter most are `generated`, which marks a stand-in at the point
 * a reader would otherwise take it for real, and `assumed`, which is our
 * reading and not yet confirmed.
 */

import type { ReactElement } from "react";
import { createElement } from "react";
import type { Bilingual, Provenance, ProvenanceKind } from "@ksidc/ontology";

export interface ProvenanceStyle {
  label: Bilingual;
  /** A short explanation of what the label means, for a tooltip. */
  meaning: Bilingual;
}

/**
 * The five kinds and what each one means.
 *
 * How they LOOK is in brand.css, not here, because the identity distinguishes
 * them by form rather than by hue: statute is a plain border, KSIDC is
 * inverted, register is dashed, generated is hatched, assumed is dotted.
 */
export const PROVENANCE_STYLES: Record<ProvenanceKind, ProvenanceStyle> = {
  statute: {
    label: { en: "Act", ml: "നിയമം" },
    meaning: {
      en: "Fixed by the Act or the Rules. The citation is the section.",
      ml: "നിയമമോ ചട്ടങ്ങളോ നിശ്ചയിച്ചത്. ഉദ്ധരണി വകുപ്പാണ്.",
    },
  },
  ksidc: {
    label: { en: "KSIDC", ml: "കെ.എസ്.ഐ.ഡി.സി" },
    meaning: {
      en: "A decision KSIDC has given us. The citation is the question and answer.",
      ml: "കെ.എസ്.ഐ.ഡി.സി നൽകിയ തീരുമാനം. ഉദ്ധരണി ചോദ്യവും ഉത്തരവുമാണ്.",
    },
  },
  register: {
    label: { en: "Register", ml: "രജിസ്റ്റർ" },
    meaning: {
      en: "Gathered from a public register. The citation names the register and the month.",
      ml: "പൊതു രജിസ്റ്ററിൽ നിന്ന് ശേഖരിച്ചത്. ഉദ്ധരണി രജിസ്റ്ററിന്റെയും മാസത്തിന്റെയും പേരാണ്.",
    },
  },
  generated: {
    label: { en: "Stand-in", ml: "മാതൃക" },
    meaning: {
      en: "This is made up, not a real record. It is here so the system can be shown working. It will be replaced when real information arrives.",
      ml: "ഇത് ഉണ്ടാക്കിയ വിവരമാണ്, യഥാർത്ഥ രേഖയല്ല. സംവിധാനം എങ്ങനെ പ്രവർത്തിക്കുന്നു എന്ന് കാണിക്കാനാണ് ഇത്. യഥാർത്ഥ വിവരം വരുമ്പോൾ ഇത് മാറ്റും.",
    },
  },
  assumed: {
    label: { en: "Assumed", ml: "അനുമാനം" },
    meaning: {
      en: "This is what we think the law means. KSIDC has not confirmed it yet. It is listed on the assumptions page.",
      ml: "നിയമം ഇങ്ങനെയാണ് എന്നാണ് ഞങ്ങൾ കരുതുന്നത്. കെ.എസ്.ഐ.ഡി.സി ഇത് സ്ഥിരീകരിച്ചിട്ടില്ല. അനുമാനങ്ങളുടെ പേജിൽ ഇത് കാണാം.",
    },
  },
};

export interface ProvenanceChipProps {
  provenance: Provenance;
  lang: "en" | "ml";
  /** Show the citation next to the label. Default true. */
  showRef?: boolean;
  className?: string;
}

/**
 * The chip. Deliberately unstyled beyond its colours so each surface can size
 * it: the applicant screens use it sparingly and the console uses it on almost
 * every line.
 */
export function ProvenanceChip({
  provenance,
  lang,
  showRef = true,
  className,
}: ProvenanceChipProps): ReactElement {
  const style = PROVENANCE_STYLES[provenance.kind] ?? PROVENANCE_STYLES.assumed;
  const label = lang === "ml" ? style.label.ml : style.label.en;
  const meaning = lang === "ml" ? style.meaning.ml : style.meaning.en;

  return createElement(
    "span",
    {
      className: ["prov", `prov-${provenance.kind}`, className].filter(Boolean).join(" "),
      title: `${meaning}${provenance.note ? `\n\n${provenance.note}` : ""}`,
    },
    createElement("b", null, label),
    showRef ? createElement("span", { className: "prov-ref" }, provenance.ref) : null,
  );
}

/** True where the value must be visibly marked as not coming from a record. */
export const needsStandInNotice = (p: Provenance): boolean => p.kind === "generated";
